import { Dialog, DialogButton, DialogRoot } from '~/components/ui/Dialog';
import { useState, useEffect } from 'react';

interface SupabaseAuthSuccessModalProps {
  isOpen: boolean;
  confirmButtonText?: string;
}

export function SupabaseAuthSuccessModal({ isOpen, confirmButtonText = 'Close Tab' }: SupabaseAuthSuccessModalProps) {
  const [open, setOpen] = useState(isOpen);
  const [countdown, setCountdown] = useState(30);

  const handleClose = () => {
    setOpen(false);

    if (!window.closed) {
      window.history.back();
    }
  };

  /*
   * Auto-close timer effect
   * Counts down from 30 seconds and automatically closes the tab when reaching 0
   */
  useEffect(() => {
    if (!isOpen || !open) {
      return undefined;
    }

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          handleClose();
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, open, handleClose]);

  if (!open || !isOpen) {
    return null;
  }

  return (
    <DialogRoot open={open && isOpen}>
      <Dialog className="max-w-[520px]" onBackdrop={handleClose} onClose={handleClose}>
        {/* Custom Header with Success Theme */}
        <div className="px-6 py-5 border-b border-alpha-elements-borderColor bg-gradient-to-r from-green-500/10 to-emerald-500/10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
              <div className="i-ph:check-circle w-6 h-6 text-green-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-alpha-elements-textPrimary flex items-center gap-2">
                Authentication Successful
              </h2>
              <div className="space-y-1">
                <p className="text-sm text-alpha-elements-textPrimary opacity-70">You can now close this tab</p>
                <p className="text-sm font-medium text-orange-400 flex items-center gap-2">
                  <div className="i-ph:timer w-4 h-4" />
                  Closing this tab in {countdown} seconds
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="px-6 py-6">
          <div className="space-y-4">
            {/* Development Environment Notice */}
            <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="i-ph:info w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="space-y-2">
                <p className="text-sm font-medium text-alpha-elements-textPrimary">Development Environment Notice</p>
                <div className="text-xs text-alpha-elements-textPrimary opacity-70 space-y-1">
                  <p>You are being redirected to Dualite because you are currently in the development environment.</p>
                  <p className="font-medium text-alpha-elements-textPrimary">
                    📝 Important: When you publish your project, users will be redirected to your deployed URL instead
                    of Dualite.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer with Centered Button */}
        <div className="px-6 pb-6 pt-2">
          <div className="flex justify-center">
            <DialogButton type="primary" onClick={handleClose}>
              <div className="flex items-center gap-2">
                <div className="i-ph:x w-4 h-4" />
                Close Now ({countdown}s)
              </div>
            </DialogButton>
          </div>
        </div>
      </Dialog>
    </DialogRoot>
  );
}
